/* eslint-disable no-use-before-define */
import { IAccount } from '@/api/api-update';
import { dateFromUTCDateTimeSecondsString } from '@/services/time-utils';
import { boolFromValue, numberFromValue } from '@/api/api-utils';

export interface IDTOAccount extends IAccount {
  isDeleted?: string | boolean, // dummy field to satisfy IApi
}

export enum BillingInfoSubscriptionStatus {
  None = 'none',
  VIP = 'vip',
  TrialActive = 'trialActive',
  SubActive = 'subActive',
  TrialWillExpire = 'trialWillExpire',
  TrialExpired = 'trialExpired',
  SubWillExpire = 'subWillExpire',
  SubWillExpirePayment = 'subWillExpirePayment',
  SubExpired = 'subExpired',
  SubExpiredPayment = 'subExpiredPayment',
}

export enum BillingInfoSMSStatus {
  None = 'none',
  VIP = 'vip',
  Active = 'active',
  LowBalance = 'lowBalance',
  ZeroBalance = 'zeroBalance',
  PaymentError = 'paymentError',
}

export interface BillingInfo {
  subStatus: BillingInfoSubscriptionStatus;
  data?: string;
  trialExpDate?: Date;
  subExpDate?: Date;
  smsStatus: BillingInfoSMSStatus;
  smsBalance: number;
}

export default class Account {
  id: number;

  accountName?: string;

  streetAddress?: string;

  postalCode?: string;

  city?: string;

  countryState?: string;

  // countryName?: string;

  countryCode?: string;

  phone?: string;

  email?: string;

  website?: string;

  facebook?: string;

  instagram?: string;

  yelp?: string;

  x?: string;

  youtube?: string;

  tripadvisor?: string;

  tiktok?: string;

  locale?: string;

  defaultLocale?: string;

  enabledLocales?: string[];

  timeZoneId?: number;

  // firstDayOfWeek?: number;

  // is12HourClock?: boolean;

  // timeZone?: string;

  employeeNameIsNeeded?: boolean;

  userId?: number;

  username?: string;

  isRegistered?: boolean;

  isPremium?: boolean;

  subscriptionExpirationDate?: Date;

  upgradeIsNeeded?: boolean;

  color?: string;

  widgetDocumentationURL?: string;

  defaultAccessKey?: string;

  logoLink: string;

  serviceManagerAccessKey?: string;

  proSubscriptionExpirationDate?: Date;

  proTrialExpirationDate?: Date;

  subStatus?: BillingInfoSubscriptionStatus;

  smsStatus?: BillingInfoSMSStatus;

  smsBalance?: number;

  proData?: string;

  features?: string[];

  apps?: string[];

  stripeDepositFee?: number;

  constructor(o?: IAccount) {
    this.id = o?.accountId ?? 0;
    this.accountName = o?.accountName;
    this.streetAddress = o?.streetAddress;
    this.postalCode = o?.postalCode;
    this.city = o?.city;
    this.countryState = o?.countryState;
    //   this.countryName = o?.country;
    this.countryCode = o?.countryCode;
    this.phone = o?.phone;
    this.email = o?.email;
    this.website = o?.website;
    this.facebook = o?.facebook;
    this.instagram = o?.instagram;
    this.yelp = o?.yelp;
    this.x = o?.x;
    this.youtube = o?.youtube;
    this.tripadvisor = o?.tripadvisor;
    this.tiktok = o?.tiktok;
    this.locale = o?.locale;
    this.defaultLocale = o?.defaultLocale ?? o?.locale ?? 'en';
    this.enabledLocales = o?.enabledLocales?.split(',') ?? [];
    this.timeZoneId = numberFromValue(o?.timeZoneId);
    //   this.timeZone = o?.timeZone;
    // this.firstDayOfWeek = o?.firstDayOfWeek;
    // this.is12HourClock = boolFromValue(o?.is12HourClock);
    this.employeeNameIsNeeded = boolFromValue(o?.employeeNameIsNeeded);
    this.userId = numberFromValue(o?.userId) ?? 0;
    this.username = o?.username ?? '';
    this.isRegistered = boolFromValue(o?.isRegistered);
    this.isPremium = boolFromValue(o?.isPremium);
    this.upgradeIsNeeded = boolFromValue(o?.upgradeIsNeeded);
    this.color = o?.color?.toUpperCase() ?? '#3355FF';

    this.widgetDocumentationURL = o?.widgetDocumentationURL;
    this.defaultAccessKey = o?.defaultAccessKey;
    this.serviceManagerAccessKey = o?.serviceManagerAccessKey;
    this.logoLink = o?.logoLink ?? ''; // empty string - no image

    this.proSubscriptionExpirationDate = dateFromUTCDateTimeSecondsString(
      o?.bProSubscriptionExpirationDate,
    ) ?? undefined;
    this.proTrialExpirationDate = dateFromUTCDateTimeSecondsString(o?.bProTrialExpirationDate) ?? undefined;
    this.subStatus = (o?.bSubscriptionInfo || BillingInfoSubscriptionStatus.None) as BillingInfoSubscriptionStatus;
    this.smsStatus = BillingInfoSMSStatus.None as BillingInfoSMSStatus; // TODO: finish api
    this.smsBalance = 10; // TODO: finish api

    this.features = o?.features ?? [];
    this.apps = o?.apps ?? [];
    this.stripeDepositFee = o?.stripeDepositFee;
  }

  toDto(): IDTOAccount {
    const dto: IDTOAccount = {
      ...this,
      enabledLocales: this.enabledLocales?.join(','),
      accountId: this.id,
      id: undefined,
      subscriptionExpirationDate: undefined,
      proSubscriptionExpirationDate: undefined,
      proTrialExpirationDate: undefined,
      subStatus: undefined,
      proData: undefined,
      smsStatus: undefined,
    };
    return dto;
  }

  static fromData(data: Pick<Account, keyof Account>): Account {
    const copy = new Account();
    Object.assign(copy, data);

    copy.subscriptionExpirationDate = fixDate(copy.subscriptionExpirationDate);
    copy.proSubscriptionExpirationDate = fixDate(copy.proSubscriptionExpirationDate);
    copy.proTrialExpirationDate = fixDate(copy.proTrialExpirationDate);

    return copy;
  }
}

function fixDate(date: Date | string | undefined): Date | undefined {
  if (typeof date === 'string') return new Date(date);
  return date;
}
